import { useState } from 'react'
import { LockOutlined, UserOutlined } from '@ant-design/icons'
import { Alert, Button, Form, Input, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { Navigate, useLocation } from 'react-router-dom'
import { translateError } from '@/shared/api/errors'
import { useAuth } from '@/shared/auth/useAuth'
import { hasTenantConfig } from '@/shared/config'
import { AuthShell } from './AuthShell'

interface LoginFormValues {
  login: string
  password: string
}

/**
 * Kirish ekrani — SODDA, faqat login va parol.
 *
 * ⚠️ `tenantCode` maydoni ATAYLAB YO'Q — abonent uni bilishi shart emas
 * (`shared/config.ts`). Backend uni talab qiladi, lekin bu build/deploy
 * konfiguratsiyasidan (subdomen) avtomatik olinadi.
 *
 * ⚠️ Admin panelining TOTP oqimi ham ATAYLAB YO'Q (buyurtma hujjati:
 * "abonent uchun ortiqcha to'siq"). Abonent oyiga bir-ikki marta kiradi,
 * ikki faktorli tekshiruv shu chastotada foyda emas — yo'qotish xavfini
 * oshiradi.
 */
export function LoginPage() {
  const { t } = useTranslation()
  const { isAuthenticated, signIn } = useAuth()
  const location = useLocation()
  const [form] = Form.useForm<LoginFormValues>()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Tenant kodi butunlay bo'lmasa — bu KONFIGURATSIYA XATOSI, so'rov
  // yuborishning ma'nosi yo'q (backend baribir rad etadi). Aniq xabar
  // ko'rsatiladi, jimgina "login yoki parol noto'g'ri" deb aytilmaydi —
  // bu ikkalasi butunlay boshqa muammo.
  const configOk = hasTenantConfig()

  if (isAuthenticated) {
    const state = location.state as { from?: { pathname: string; search: string } } | null
    const from = state?.from
    const to = from ? `${from.pathname}${from.search}` : '/'
    return <Navigate to={to} replace />
  }

  const handleFinish = (values: LoginFormValues) => {
    setError(null)
    setSubmitting(true)
    signIn(values.login, values.password)
      .catch((err: unknown) => {
        setError(translateError(err, t))
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  return (
    // ⚠️ Sarlavha "X-SIB" emas, "Kirish": mahsulot nomi endi qobiqning
    // o'zida (keng ekranda chap ustunda, torda karta tepasida) turadi va
    // ikki marta takrorlangani formani pastga surib yuborardi.
    <AuthShell title={t('auth.title')} subtitle={t('auth.subtitle')}>
      {!configOk ? (
        <Alert type="error" showIcon message={t('errors.tenantConfigMissing')} />
      ) : null}
      {error ? <Alert type="error" showIcon message={error} /> : null}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        disabled={submitting || !configOk}
        requiredMark={false}
      >
        <Form.Item
          name="login"
          label={t('auth.username')}
          rules={[{ required: true, message: t('auth.username') }]}
        >
          <Input
            autoComplete="username"
            autoFocus
            size="large"
            prefix={<UserOutlined />}
            inputMode="text"
          />
        </Form.Item>

        <Form.Item
          name="password"
          label={t('auth.password')}
          rules={[{ required: true, message: t('auth.password') }]}
        >
          <Input.Password autoComplete="current-password" size="large" prefix={<LockOutlined />} />
        </Form.Item>

        <Form.Item style={{ marginBottom: 0 }}>
          <Button type="primary" htmlType="submit" size="large" block loading={submitting}>
            {submitting ? t('auth.signingIn') : t('auth.signIn')}
          </Button>
        </Form.Item>
      </Form>

      {/*
        ⛔ DOIMIY YO'L-YO'RIQ — xato matnining ichida EMAS.

        Kabinet paroli internetga ulanish parolidan BOSHQA: u alohida
        jadvalda (`selfcare.credential`) va uni XODIM o'rnatadi
        (`POST /api/selfcare-admin/subscribers/{id}/selfcare-password`).
        Bugungi holatda ko'p abonentda u UMUMAN YO'Q.

        Server har rad javobini bitta matnga yig'adi — «Login yoki parol
        noto'g'ri» — va bu ATAYLAB (enumeration himoyasi,
        `CredentialService` javadoc'i). Ya'ni «parolingiz o'rnatilmagan»
        deb aytish MUMKIN EMAS.

        Natijada abonent internet parolini kiritadi, rad javobini oladi
        va keyingi qadamni HECH QAYERDAN bilmaydi — kabinetni «buzuq»
        deb hisoblaydi. Jonli ekranda tasdiqlandi (2026-08-23).

        Shuning uchun yo'l-yo'riq xatoga BOG'LANMAGAN: u har doim
        ko'rinadi va rad javobining sababini oshkor qilmaydi.
      */}
      <Typography.Paragraph
        type="secondary"
        style={{ fontSize: 13, marginTop: 16, marginBottom: 0 }}
      >
        {t('auth.passwordHint')}
      </Typography.Paragraph>
    </AuthShell>
  )
}
